import { useState } from 'react';
import Script from 'next/script';

import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { LayoutContainer } from 'components/Layout';
import { useEffectOnce } from 'hooks';
import type { Field, SitecoreBlockProps } from 'models/sitecore';
import type { Flowbox as FlowboxApi } from 'models/vendor';
import { getEditorMargin } from 'utils/business-logic';

interface Props extends SitecoreBlockProps {
	fields?: {
		id: Field<string>;
		key: Field<string>;
		locale?: Field<string>;
	};
}

export default function Flowbox({ fields, params }: Props) {
	const id = fields?.id?.value;
	const key = fields?.key?.value;
	const locale = fields?.locale?.value || 'sv-SE';
	const [didInit, setDidInit] = useState(false);

	const initFlowbox = () => {
		if (didInit || !key || !id) {
			return;
		}
		// The relevant part from the Flowbox snippet, ensuring a global flowbox
		// function is available for the init call.
		// https://help.getflowbox.com/en/articles/2550100-embedding-a-static-flow-on-your-website
		if (!globalThis.flowbox) {
			const f: FlowboxApi = (...args) => {
				f.q.push(args);
			};
			f.q = [];
			globalThis.flowbox = f;
		}
		globalThis.flowbox('init', { container: `#${id}`, key, locale });
		setDidInit(true);
	};

	// For router navigation where the script is already loaded.
	useEffectOnce(() => {
		initFlowbox();
	});

	if (!id || !key) {
		return (
			<ComponentPlaceholder
				componentName="Flowbox"
				description="Requires an id and a key"
				className={getEditorMargin(params)}
			/>
		);
	}

	return (
		<LayoutContainer outerClassName={getEditorMargin(params)}>
			{/* Negative margin to align with the edge, items have outer padding. */}
			<div className="-mx-2 min-h-240" id={id} />
			<Script
				src="https://connect.getflowbox.com/flowbox.js"
				id="flowbox-js"
				strategy="afterInteractive"
				onLoad={initFlowbox}
			/>
		</LayoutContainer>
	);
}
Flowbox.displayName = 'Flowbox';
